import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb';
import log from 'loglevel';
import moment from 'moment';
import {
    ITrainingModuleState,
    ITrainingProgress,
    ITrainingProgressService,
    IUpdateTrainingProgressForCurrentUserRequest,
} from '@sparx/grpc/lib/generated/apis/sparx/training/progress/v1/trainingprogress';
import { showAlert } from 'phoenix2/state/alert/sagas';
import { VIDEO_TYPE_TUTORIAL } from 'phoenix2/state/video/video';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
    completeTrainingTask,
    setTrainingModule,
    setNeedHelpVideo,
    setWatchingHelpVideo,
    updateStoredTrainingProgress,
    resyncTrainingProgress,
    resetTrainingProgress,
    startTraining,
    calculateModuleCompletion,
    setSelectedStep,
    submitUserRatingFeedback,
    showFeedbackDialog,
} from './actions';
import { ModuleCache, TrainingState } from './reducers';
import {
    getCurrentModule,
    getTrainingModeCurrentStep,
    getTrainingModuleByName,
    getTrainingModules,
} from './selectors';
import {
    getTrainingStepID,
    IntroStep,
    isCompletable,
    isIntro,
    isVideo,
    Module,
    Step,
    TrainingModuleName,
    UnknownModuleError,
    UnknownStepError,
    UpdatePayload,
} from './types';
import {
    clickedOnNextStepEvent,
    clickedViewIntroVideoEvent,
    clickedViewVideoEvent,
    clickOnContinueToTrainingEvent,
} from '../../components/widgets/training/analytics';
import { asyncSleep } from 'phoenix2/utils';
import { wCall } from 'phoenix2/state/wait/sagas';
import {
    IRating,
    ISubmitUserRatingFeedbackRequest,
    ITeacherFeedbackService,
} from '@sparx/grpc/lib/generated/apis/sparx/interaction/feedback/v1/feedback';
import { selectCurrentView } from 'phoenix2/state/views/selectors';
import { View } from '../views/reducers';
import { StudentExperienceTrainingStep } from 'phoenix2/content/training/training-modules';

const RETRY_DELAY_SECS = 1;

/**
 * Sagas for the teacher training module in Sparx Web which shows a teacher how to use the student
 * experience.
 *
 * Copied from tpclient2 'reducers/training/index.ts', but adapted to not use redux toolkit
 */

/**
 * Returns the first step in a training module that the user has not yet completed. If they have
 * completed all steps, returns undefined
 * @param steps all the steps in a training module
 */
export const firstIncompleteStep = (steps: Step[]): number | undefined => {
    const step = steps.findIndex(s => isCompletable(s) && !s.isComplete);

    // If all steps are completed, just set to the last step:
    if (step === -1 && steps.length > 0) {
        return steps.length - 1;
    }

    return step === -1 ? undefined : step;
};

/**
 * Show the intro video if training is enabled and the intro step is not yet complete
 * @param needHelp When true this overrides the isComplete check on the intro step to
 * allow for the video to be watched again
 */
export function* showIntroVideo(needHelp = false) {
    const currentModule: Module | undefined = yield select(getCurrentModule);
    // Don't show if training module is disabled:
    if (currentModule === undefined) {
        return;
    }

    // Record whether the user reached the end
    let reachedEnd = false;

    // Record whether the user clicked on go to next step
    let goToNextStep = false;

    // Find the intro video step:
    const introStep = currentModule.steps.find((step): step is IntroStep => isIntro(step));
    if (introStep !== undefined && (!introStep.isComplete || needHelp)) {
        // Show the intro video:
        yield call(showAlert, {
            type: 'trainingModeVideo',
            title: introStep.title,
            noDefaultButton: true,
            message: {
                videoId: introStep.video,
                context: introStep.videoContext,
                isStepComplete: introStep.isComplete,
                instructionText: introStep.videoInstruction,
                // Callback if the user watches the video right to the end
                onReachedVideoEnd: () => (reachedEnd = true),
                // Callback for the user clicking on next step
                onNextStep: () => (goToNextStep = true),
                onEndAlert: function*() {
                    yield put(clickOnContinueToTrainingEvent());
                },
            },
            className: 'alert-box-video training-video',
            // Allows the alert to take up the whole screen with no border for small
            // devices:
            allowFullscreen: true,
        });

        // If the user reached the end of the video, record the step complete:
        if (reachedEnd) {
            yield put(
                completeTrainingTask({
                    moduleName: currentModule.name,
                    stepID: getTrainingStepID(introStep),
                }),
            );
        }

        // If the user clicked on next step then go to the next step
        if (goToNextStep) {
            yield put(clickedOnNextStepEvent(introStep, 1));
            yield put(setSelectedStep(1));
        }
    }
}

export function* showTrainingFeedbackDialogAlert(rating: IRating) {
    const trainingModule: Module = yield select(getCurrentModule);
    const currentView: View = yield select(selectCurrentView);

    let comment = '';

    yield call(showAlert, {
        type: 'feedbackAlert',
        title: 'Give additional feedback on this training module',
        noDefaultButton: true,
        className: 'training-mode-feedback-dialog',
        message: {
            onSubmitFeedbackComment: (feedbackComment: string) => (comment = feedbackComment),
        },
    });

    if (comment !== '') {
        yield put(
            submitUserRatingFeedback({
                event: `Training/ModuleSummaryFeedback/${trainingModule.name}`,
                rating,
                comment,
                labels: {
                    trainingModule: trainingModule.name,
                    moduleState: trainingModule.isComplete ? 'complete' : 'not-complete',
                    opinion: '' + rating.opinion,
                },
                tp: {
                    pageName: currentView.type,
                    pageUrl: window.location.toString(),
                    triggerName: 'training-module-useful',
                    schoolName: window.settings.schoolName,
                    applicationVersion: '',
                },
            }),
        );
    }
}

/**
 * The main saga for handling the various training mode calls
 */
export function* trainingModeSaga(
    trainingProgress: ITrainingProgressService,
    feedback: ITeacherFeedbackService,
) {
    /**
     * When the app starts, we will start the teacher training. The user's progress
     * is loaded and then this is used to initialise the Redux training state.
     * Show a loading spinner while the training progress is fetched
     */
    function* handleStartTraining({ payload }: ReturnType<typeof startTraining>) {
        yield wCall(doStartTraining, payload);
    }

    function* doStartTraining(moduleName: TrainingModuleName) {
        let progress: ITrainingProgress | null = null;
        let attemptCount = 0;
        while (attemptCount < 5) {
            attemptCount++;
            try {
                progress = yield call(trainingProgress.getTrainingProgressForCurrentUser, {});
                break;
            } catch (err) {
                log.error('Error while loading training progress', err);
                // Retry after a short delay
                yield call(asyncSleep, RETRY_DELAY_SECS);
            }
        }
        yield put(resyncTrainingProgress(progress));
        const modules: ModuleCache = yield select((state: TrainingState) => state.modules);
        for (const m in modules) {
            const md = modules[m];
            if (md.isComplete && progress && !progress.moduleData[m]?.completedTime) {
                yield put(updateStoredTrainingProgress({ module: md, force: true }));
            }
        }
        yield put(setTrainingModule(moduleName));
    }

    /**
     * Show the intro video if not already done, and training mode is enabled
     */
    function* handleSetTrainingModule() {
        yield call(showIntroVideo);
    }

    /**
     * Show the need help video for the current step
     */
    function* showNeedHelpVideo() {
        const step: Step = yield select(getTrainingModeCurrentStep);
        const currentModule: Module | undefined = yield select(getCurrentModule);

        // Don't show if training module is disabled or step is not completable:
        if (currentModule === undefined || !isCompletable(step)) {
            return;
        }

        yield put(setWatchingHelpVideo(true));

        // If it's the intro step defer to showIntroVideo
        if (isIntro(step)) {
            yield put(clickedViewIntroVideoEvent());
            yield showIntroVideo(true);
            yield put(setWatchingHelpVideo(false));
            return;
        }

        const videoId = isVideo(step) ? step.video : step.guidanceVideo;
        const [, trainingStepID] = step.id.split('/');

        let hasVideoFinished = false;

        if (videoId) {
            yield put(clickedViewVideoEvent(step));
            // Show the help video:
            yield call(showAlert, {
                type: 'video',
                title: step.title,
                noDefaultButton: true,
                message: {
                    videoId: videoId,
                    skillId: undefined,
                    activityIndex: undefined,
                    skipToFinalFrame: false,
                    videoType: VIDEO_TYPE_TUTORIAL,
                    disableSkipButton: false,
                    showSubtitles: true,
                    useSecondaryButton: true,
                    onReachedVideoEnd: () => (hasVideoFinished = true),
                    outroLength: isVideo(step) ? 5 : 0,
                },
                className: 'alert-box-video training-video',
                // Allows the alert to take up the whole screen with no border for small
                // devices:
                allowFullscreen: true,
            });
        }

        if (isVideo(step)) {
            if (trainingStepID === StudentExperienceTrainingStep.BOOKWORK_VIDEO_WATCHED) {
                yield call(showAlert, {
                    title: 'Completing Bookwork Checks',
                    type: 'trainingBookworkChecks',
                    buttons: [{ text: 'Continue training', key: 'close' }],
                });
            }

            if (hasVideoFinished) {
                yield put(
                    completeTrainingTask({
                        moduleName: currentModule.name,
                        stepID: getTrainingStepID(step),
                    }),
                );
            }
        }

        yield put(setWatchingHelpVideo(false));
    }

    /**
     * Handle a module step being completed by updating redux state and making a request to the
     * server to update the module with the completed step.
     * @param payload The moduleName and stepID for the step being completed
     */
    function* handleCompleteTrainingStep({ payload }: ReturnType<typeof completeTrainingTask>) {
        const currentModule = yield select(getCurrentModule);
        if (currentModule === undefined) {
            return;
        }
        const modules: ModuleCache = yield select(getTrainingModules);
        const { moduleName, stepID } = payload;
        const moduleStepKey = `${moduleName}/${stepID}`;

        const m = modules[moduleName];
        if (!m) {
            throw new UnknownModuleError(moduleName);
        }
        const step: Step | undefined = m.steps[m.stepMap[moduleStepKey]];
        if (step === undefined) {
            throw new UnknownStepError(moduleStepKey);
        }
        if (isCompletable(step)) {
            yield put(calculateModuleCompletion(moduleName));
            yield put(updateStoredTrainingProgress({ module: m }));
        }
    }

    /**
     * Update the value in the store for the given module for the following
     * completed_steps Find all completed steps in redux since some may be new
     * completed_time The current time if the module has just been completed
     * @param payload UpdatePayload, the new module data to be saved to the store and whether to
     * force assessment of values that are dynamically set
     */
    function* handleUpdateStoredTrainingProgress({
        payload: { module: m, force },
    }: {
        payload: UpdatePayload;
    }) {
        const moduleState: Module = yield select(getTrainingModuleByName(m.name));
        const data: Partial<ITrainingModuleState> = {
            completedSteps: moduleState.steps
                .filter(s => isCompletable(s) && s.isComplete)
                .map(s => s.id.split('/')[1]),
            annotations: { 'sparx.schools/name': 'schools/' + window.settings.schoolID },
        };
        const mask = new FieldMask().setPathsList(['module_data.completed_steps', 'annotations']);

        // If we are forcing an update, or the module passed into the saga is complete but the one
        // in redux isn't and thus is newly completed, update completed_time to now.
        if (force || (!m.isComplete && moduleState.isComplete)) {
            data.completedTime = moduleState.isComplete ? moment() : undefined;
            mask.addPaths('module_data.completed_time');
        }
        // We coerce the type here as most of the fields are not optional on the type
        // (the type is wrong, but generated)
        const req = ({
            trainingProgress: {
                moduleData: {
                    [moduleState.name]: data,
                },
            },
            updateMask: mask,
        } as unknown) as IUpdateTrainingProgressForCurrentUserRequest;
        let attemptCount = 0;
        // Try updating up to 5 times
        while (attemptCount < 5) {
            attemptCount++;
            try {
                yield call(trainingProgress.updateTrainingProgressForCurrentUser, req);
                break;
            } catch (err) {
                // eslint-disable-next-line no-console
                log.error('Error updating progress', err);
                // Short delay before retrying
                yield call(asyncSleep, RETRY_DELAY_SECS);
            }
        }
    }

    /**
     * Reset all training modules for the current user. This will reset all module data including
     * any completed steps and module completion times
     */
    function* handleResetTrainingProgress() {
        const moduleData = {};
        const modules: ModuleCache = yield select(getTrainingModules);
        for (const m in modules) {
            moduleData[m] = {};
        }
        // We coerce the type here as most of the fields are not optional on the type
        // (the type is wrong, but generated)
        const req = ({
            trainingProgress: {
                moduleData,
            },
            updateMask: new FieldMask().setPathsList([
                'module_data.completed_steps',
                'module_data.completed_time',
                'module_data.dismiss_time',
            ]),
        } as unknown) as IUpdateTrainingProgressForCurrentUserRequest;
        try {
            yield call(trainingProgress.updateTrainingProgressForCurrentUser, req);
        } catch (err) {
            // eslint-disable-next-line no-console
            log.error('Error resetting progress', err);
        }
    }

    function* handleSubmitUserRatingFeedback({
        payload,
    }: {
        payload: ISubmitUserRatingFeedbackRequest;
    }) {
        try {
            yield call(feedback.submitUserRatingFeedback, payload);
        } catch (err) {
            // eslint-disable-next-line no-console
            log.error('Error submitting user rating feedback', err);
        }
    }

    function* handleShowFeedbackDialog({ payload }: { payload: IRating }) {
        yield call(showTrainingFeedbackDialogAlert, payload);
    }

    yield all([
        takeEvery(startTraining, handleStartTraining),
        takeEvery(setTrainingModule, handleSetTrainingModule),
        takeEvery(setNeedHelpVideo, showNeedHelpVideo),
        takeEvery(completeTrainingTask, handleCompleteTrainingStep),
        takeEvery(updateStoredTrainingProgress, handleUpdateStoredTrainingProgress),
        takeEvery(resetTrainingProgress, handleResetTrainingProgress),
        takeEvery(submitUserRatingFeedback, handleSubmitUserRatingFeedback),
        takeEvery(showFeedbackDialog, handleShowFeedbackDialog),
    ]);
}
